import React, { useMemo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import FieldLabel from '../components/FieldLabel';
import { getFieldLabel } from '../utils/formHelpers';
import { outlinedFieldProps } from '../utils/fieldStyles';

/**
 * Searchable single-select lookup backed by options resolved in StepForm
 * (field.optionsSource → resolveLookupOptions).
 *
 * Optional: field.filterMerchantsByLogistics = 'package_component_id'
 * filters merchant options to:
 * - merchants whose service type matches the selected trip logistics, and
 * - merchants with DMC (shown for every logistics type).
 */
const CATEGORY_TO_SERVICE_TYPES = {
    accommodation: ['accommodation'],
    transportation: ['transport', 'transfer', 'transportation'],
    activity: ['activity'],
    meal: ['meal', 'meals'],
    guide: ['guide'],
    other: ['other', 'rental'],
};

const CATEGORY_TO_ASSET_TYPES = {
    accommodation: ['accommodation'],
    transportation: ['transport', 'transfer'],
    activity: ['activity'],
    meal: ['meal', 'meals'],
    guide: ['guide'],
    other: ['other', 'rental'],
};

const normalizeToken = (value) => String(value || '').toLowerCase().replace(/[\s_-]+/g, '');

const tokenMatchesAllowed = (value, allowed) => {
    const type = normalizeToken(value);
    if (!type) return false;
    return allowed.some((token) => type.includes(token) || token.includes(type));
};

const serviceTypeMatchesCategory = (serviceType, category) => {
    const cat = normalizeToken(category);
    if (!cat) return false;
    const allowed = CATEGORY_TO_SERVICE_TYPES[cat] || [cat];
    return tokenMatchesAllowed(serviceType, allowed);
};

const assetTypeMatchesCategory = (assetType, category) => {
    const cat = normalizeToken(category);
    if (!cat) return false;
    const allowed = CATEGORY_TO_ASSET_TYPES[cat] || [cat];
    return tokenMatchesAllowed(assetType, allowed);
};

const merchantHasDmc = (merchant) => {
    if (merchant?.isDmc) return true;
    const serviceTypes = merchant?.serviceTypes || [];
    return serviceTypes.some((value) => normalizeToken(value) === 'dmc');
};

const SearchableLookupField = ({
    field,
    data,
    change,
    hasError,
    errorMessage,
    resolveLookupOptions,
}) => {
    const options = useMemo(() => {
        let resolved = resolveLookupOptions?.(field) || field.options || [];

        const logisticsFieldKey = field.filterMerchantsByLogistics;
        if (logisticsFieldKey && field.optionsSource === 'merchants') {
            const logisticsId = data?.[logisticsFieldKey];
            if (!logisticsId) {
                return [];
            }
            const logisticsOptions = resolveLookupOptions?.({ optionsSource: 'trip_logistics' }) || [];
            const selectedLogistics = logisticsOptions.find(
                (opt) => String(opt.value) === String(logisticsId)
            );
            const category = selectedLogistics?.category;
            const currentMerchantId = data?.[field.key];

            resolved = resolved.filter((merchant) => {
                if (currentMerchantId != null && String(merchant.value) === String(currentMerchantId)) {
                    return true;
                }
                if (merchantHasDmc(merchant)) {
                    return true;
                }
                const serviceTypes = merchant.serviceTypes || [];
                if (category && serviceTypes.some((serviceType) =>
                    serviceTypeMatchesCategory(serviceType, category)
                )) {
                    return true;
                }
                const linkedIds = merchant.logisticsIds || [];
                if (linkedIds.some((id) => String(id) === String(logisticsId))) {
                    return true;
                }
                const assetTypes = merchant.assetTypes || [];
                return category
                    ? assetTypes.some((assetType) => assetTypeMatchesCategory(assetType, category))
                    : false;
            });
        }

        return resolved;
    }, [resolveLookupOptions, field, data]);

    const selectedValue = data?.[field.key];
    const selected = useMemo(() => {
        if (selectedValue == null || selectedValue === '') return null;
        const match = options.find((opt) => String(opt.value) === String(selectedValue));
        if (match) return match;
        // Keep selected label visible even if option list is still loading.
        return { value: selectedValue, label: `Selected (#${selectedValue})` };
    }, [options, selectedValue]);

    const optionsWithSelected = useMemo(() => {
        if (!selected) return options;
        if (options.some((opt) => String(opt.value) === String(selected.value))) {
            return options;
        }
        return [selected, ...options];
    }, [options, selected]);

    const logisticsGate = field.filterMerchantsByLogistics;
    const logisticsSelected = logisticsGate ? Boolean(data?.[logisticsGate]) : true;
    const isTripLogistics = field.optionsSource === 'trip_logistics';
    const placeholder = !logisticsSelected && field.optionsSource === 'merchants'
        ? 'Select trip logistics first...'
        : (field.placeholder || 'Search...');

    return (
        <Autocomplete
            options={optionsWithSelected}
            value={selected}
            openOnFocus
            autoHighlight
            disabled={Boolean(logisticsGate && !logisticsSelected)}
            onChange={(_event, next) => change(field.key, next ? next.value : '')}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(option, value) =>
                String(option?.value) === String(value?.value)
            }
            filterOptions={(opts, state) => {
                const input = String(state.inputValue || '').trim().toLowerCase();
                if (!input) return opts;
                return opts.filter((opt) =>
                    String(opt?.label || '').toLowerCase().includes(input)
                );
            }}
            noOptionsText={
                field.optionsSource === 'merchants'
                    ? (logisticsSelected
                        ? 'No matching or DMC merchants found'
                        : 'Select trip logistics first')
                    : (isTripLogistics
                        ? 'No trip logistics for this destination — add some in the Trip Logistics step'
                        : 'No options')
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    {...outlinedFieldProps}
                    label={<FieldLabel label={getFieldLabel(field)} tooltip={field.tooltip} />}
                    placeholder={placeholder}
                    error={hasError}
                    helperText={hasError ? (errorMessage || 'This field is required') : ''}
                />
            )}
        />
    );
};

export default SearchableLookupField;
